import { InfoOutlineIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  Heading,
  IconButton,
  Input,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Spinner,
  Text,
  Textarea,
  VStack,
  chakra,
  useToast,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import JsonView from "@uiw/react-json-view";
import { isEmpty } from "lodash";
import React from "react";
import { ResponseOf } from "../../../core/api";
import { CopyToClipboard } from "../../../shared/components/CopyToClipboard";
import Page from "../../../shared/components/Page";
import Select from "../../../shared/components/Select";
import useApi from "../../../shared/hooks/useApi";
import { usePromptTestingRun } from "../prompt-testing.mutations";

export default function PromptTestingRoute() {
  const { queries } = useApi();
  const schemasQuery = useQuery(queries.promptTesting.getSchemas());

  if (schemasQuery.isLoading || schemasQuery.isPending) {
    return (
      <Page>
        <Box alignItems="center" display="flex" justifyContent="center" minH="400px">
          <Spinner size="xl" />
        </Box>
      </Page>
    );
  }

  if (schemasQuery.isError) {
    return (
      <Page>
        <Alert status="error">
          <AlertIcon />
          Failed to load schemas. Please try again later.
        </Alert>
      </Page>
    );
  }

  return <PromptTestingRouteLoaded initialData={schemasQuery.data} />;
}

function PromptTestingRouteLoaded(props: {
  initialData: ResponseOf<"get", "./prompt_testing/schemas">;
}) {
  const [systemPrompt, setSystemPrompt] = React.useState("");
  const [userPrompt, setUserPrompt] = React.useState("");
  const [schemaId, setSchemaId] = React.useState(props.initialData.schemas[0].id);
  const [model, setModel] = React.useState(props.initialData.defaultModel);
  const [temperature, setTemperature] = React.useState(props.initialData.defaultTemperature);
  const [result, setResult] = React.useState<object | null>(null);

  const toast = useToast({ position: "top-right" });
  const runMutation = usePromptTestingRun({
    onSuccess: (response) => {
      if (response.success) {
        setResult(
          typeof response.result === "string" ? JSON.parse(response.result) : response.result
        );

        toast({
          description: "Prompt test completed",
          duration: 3000,
          isClosable: true,
          status: "success",
          title: "Success",
        });
      } else {
        setResult({ error: response.error ?? "Test failed with no error message" });
        toast({
          description: response.error ?? "Test failed",
          duration: 5000,
          isClosable: true,
          status: "error",
          title: "Test Failed",
        });
      }
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : "Failed to run prompt test";
      setResult({ error: `Error: ${errorMessage}` });
      toast({
        description: errorMessage,
        duration: 5000,
        isClosable: true,
        status: "error",
        title: "Error",
      });
    },
  });

  const handleClickRun = () => {
    if (isEmpty(systemPrompt) || isEmpty(userPrompt) || isEmpty(schemaId)) {
      toast({
        description: "Please fill in all required fields",
        duration: 3000,
        isClosable: true,
        status: "warning",
        title: "Missing fields",
      });
      return;
    }

    runMutation.mutate({
      systemPrompt,
      userPrompt,
      schemaId,
      model: model,
      temperature: temperature,
    });
  };

  const resultLength = React.useMemo(() => {
    return result === null ? 0 : JSON.stringify(result).length;
  }, [result]);

  const selectedSchema = props.initialData.schemas.find((x) => x.id === schemaId);

  return (
    <Page>
      <Page.Content>
        <VStack align="stretch" p={4} spacing={4}>
          <Heading size="lg">Prompt Testing</Heading>

          {/* Controls */}
          <Box bg="white" borderRadius="md" boxShadow="sm" p={4}>
            <Grid gap={4} templateColumns="repeat(3, 1fr)">
              <GridItem>
                <FormControl isRequired>
                  <HStack>
                    <FormLabel>Schema</FormLabel>
                    <Select
                      allowUnselect={false}
                      label="Select schema"
                      multiple={false}
                      options={props.initialData.schemas.map((s) => ({
                        label: s.name,
                        value: s.id,
                      }))}
                      value={schemaId}
                      onChange={(s) => {
                        if (s !== undefined) setSchemaId(s);
                      }}
                    />
                    <Popover>
                      <PopoverTrigger>
                        <IconButton
                          aria-label="schema format"
                          colorScheme="blue"
                          icon={<InfoOutlineIcon />}
                          variant="outline"
                        />
                      </PopoverTrigger>
                      <PopoverContent w="fit-content">
                        <PopoverArrow />
                        <PopoverCloseButton />
                        <PopoverHeader>Schema information</PopoverHeader>
                        <PopoverBody>
                          <CopyToClipboard text={JSON.stringify(selectedSchema?.format)} />
                          <JsonView displayDataTypes={false} value={selectedSchema?.format} />
                        </PopoverBody>
                      </PopoverContent>
                    </Popover>
                  </HStack>
                </FormControl>
              </GridItem>

              <GridItem>
                <FormControl isRequired>
                  <HStack>
                    <FormLabel>Model</FormLabel>

                    <Select
                      allowUnselect={false}
                      label="Select Model"
                      multiple={false}
                      options={props.initialData.models.map((x) => ({ label: x, value: x }))}
                      value={model}
                      onChange={(model) => {
                        if (model !== undefined) setModel(model);
                      }}
                    />
                  </HStack>
                </FormControl>
              </GridItem>

              <GridItem>
                <FormControl isRequired>
                  <HStack>
                    <FormLabel>Temperature:</FormLabel>
                    <Input
                      type="number"
                      value={temperature}
                      onChange={(e) =>
                        setTemperature(parseFloat(parseFloat(e.currentTarget.value).toFixed(2)))
                      }
                    />
                  </HStack>
                </FormControl>
              </GridItem>
            </Grid>

            <Button
              colorScheme="blue"
              isLoading={runMutation.isPending}
              loadingText="Running..."
              mt={4}
              width="full"
              onClick={handleClickRun}
            >
              Run Test
            </Button>
          </Box>

          {/* Three Column Layout */}
          <Grid gap={4} templateColumns="repeat(3, 1fr)">
            {/* System Prompt Column */}
            <HeighGridItem>
              <Box bg="white" borderRadius="md" boxShadow="sm" h="full" p={4}>
                <VStack align="stretch" h="full" spacing={3}>
                  <Heading size="md">System Prompt</Heading>
                  <Textarea
                    flex={1}
                    fontFamily="monospace"
                    fontSize="sm"
                    placeholder="Enter system prompt..."
                    resize="none"
                    value={systemPrompt}
                    onChange={(e) => setSystemPrompt(e.target.value)}
                  />
                  <Text color="gray.500" fontSize="xs">
                    Characters: {systemPrompt.length}
                  </Text>
                </VStack>
              </Box>
            </HeighGridItem>

            {/* User Prompt Column */}
            <HeighGridItem>
              <Box bg="white" borderRadius="md" boxShadow="sm" h="full" p={4}>
                <VStack align="stretch" h="full" spacing={3}>
                  <Heading size="md">User Prompt</Heading>
                  <Textarea
                    flex={1}
                    fontFamily="monospace"
                    fontSize="sm"
                    placeholder="Enter user prompt..."
                    resize="none"
                    value={userPrompt}
                    onChange={(e) => setUserPrompt(e.target.value)}
                  />
                  <Text color="gray.500" fontSize="xs">
                    Characters: {userPrompt.length}
                  </Text>
                </VStack>
              </Box>
            </HeighGridItem>

            {/* Result Column */}
            <HeighGridItem>
              <Box bg="white" borderRadius="md" boxShadow="sm" h="full" p={4}>
                <VStack align="stretch" h="full" spacing={3}>
                  <HStack>
                    <Heading size="md">Result</Heading>
                    {result && (
                      <CopyToClipboard
                        text={JSON.stringify(result)}
                        onCopied={() =>
                          toast({
                            title: "Copied to clipboard",
                            status: "success",
                          })
                        }
                      />
                    )}
                  </HStack>
                  {runMutation.isPending ? (
                    <Box alignItems="center" display="flex" flex={1} justifyContent="center">
                      <Spinner size="lg" />
                    </Box>
                  ) : (
                    <>
                      <Box
                        bg="gray.50"
                        borderRadius="md"
                        flex={1}
                        fontFamily="monospace"
                        fontSize="sm"
                        // maxH="calc(100vh - 450px)"
                        overflowY="auto"
                        p={3}
                        whiteSpace="pre-wrap"
                      >
                        {result === null ? (
                          "No result yet. Run a test to see the output."
                        ) : (
                          <JsonView displayDataTypes={false} value={result} />
                        )}
                      </Box>
                      <Text color="gray.500" fontSize="xs">
                        Characters: {resultLength}
                      </Text>
                    </>
                  )}
                </VStack>
              </Box>
            </HeighGridItem>
          </Grid>
        </VStack>
      </Page.Content>
    </Page>
  );
}

const HeighGridItem = chakra(GridItem, {
  baseStyle: {
    maxH: "calc(100vh - 350px)",
    h: "calc(100vh - 350px)",
  },
});
